import { useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type { BrowseTournamentsPage } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The discoverable tournament browse page (GET /tournaments → {items, total,
 * limit, offset}, anonymous-readable). Keyed by (status, offset) so each filter
 * + page lives in a DISTINCT cache slot — switching the status filter or paging
 * never reads a stale list. The page size is fixed; the page renders the
 * server's order verbatim (no client re-sort) and paginates off the REAL
 * `total`, never a fabricated count.
 */
export function useTournaments(params: {
  q?: string;
  status?: string;
  tournament_type?: "individual" | "team";
  event_mode?: "singles" | "doubles" | "mixed_doubles";
  event_grade?: "A" | "B" | "C" | "D" | "J";
  city?: string;
  facility_id?: string;
  starts_after?: string;
  lat?: number;
  lng?: number;
  radius_km?: number;
  official?: boolean;
  limit: number;
  offset: number;
}) {
  return useQuery<BrowseTournamentsPage>({
    queryKey: [
      ...queryKeys.tournaments.browse(params.status, params.offset),
      params,
    ],
    queryFn: () =>
      tournamentApi.browse({
        ...(params.q ? { q: params.q } : {}),
        status: params.status,
        ...(params.tournament_type
          ? { tournament_type: params.tournament_type }
          : {}),
        ...(params.event_mode ? { event_mode: params.event_mode } : {}),
        ...(params.event_grade ? { event_grade: params.event_grade } : {}),
        ...(params.city ? { city: params.city } : {}),
        ...(params.facility_id ? { facility_id: params.facility_id } : {}),
        ...(params.starts_after ? { starts_after: params.starts_after } : {}),
        ...(params.lat !== undefined ? { lat: params.lat } : {}),
        ...(params.lng !== undefined ? { lng: params.lng } : {}),
        ...(params.radius_km !== undefined
          ? { radius_km: params.radius_km }
          : {}),
        ...(params.official ? { official: true } : {}),
        limit: params.limit,
        offset: params.offset,
      }),
  });
}
